import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { DataGrid, NoRowsOverlay } from 'src/components/DataGrid';
import { GridColDef, GridRowModel } from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslate } from '@im/react-localization';
import {
  EnduserAffiliationList,
  useEnduserAffiliationListQuery,
  useEnduserListQuery,
} from '../../hooks/deal-details';

interface AffiliatedEndUserDialogProps {
  enduser: string;
  handleClose: () => void;
  isAffiliated: boolean;
}
export default function AffiliatedEndUserDialog(
  props: Readonly<AffiliatedEndUserDialogProps>
) {
  const translate = useTranslate();
  const EnduserAffiliationListData = useEnduserAffiliationListQuery(
    {
      bidId: props.enduser,
    },
    props.isAffiliated
  );
  const endUserList = useEnduserListQuery(
    { bidId: props.enduser },
    !props.isAffiliated
  );
  const rows = props.isAffiliated
    ? EnduserAffiliationListData.data?.result ?? []
    : endUserList.data ?? [];
  const columnHeaders = [
    {
      field: 'endUserName',
      headerName: translate('Common.Name'),
    },
    {
      field: 'address',
      headerName: translate('Common.Address'),
    },
    {
      field: 'city',
      headerName: translate('Common.City'),
    },
    {
      field: 'state',
      headerName: translate('Common.State'),
    },
    {
      field: 'country',
      headerName: translate('Common.Country'),
    },
    {
      field: 'postalCode',
      headerName: translate('Common.PostalCode'),
    },
  ];
  const columnHeadersForEndUser = [
    {
      field: 'endUserName',
      headerName: translate('Common.Name'),
    },
    {
      field: 'endUserNumber',
      headerName: translate('VendorProgram.EnduserNumber'),
    },
  ];
  const columns: GridColDef<EnduserAffiliationList>[] = (
    props.isAffiliated ? columnHeaders : columnHeadersForEndUser
  ).map((column) => ({
    field: column.field,
    flex: 1,
    headerName: column.headerName,
    renderCell: (item) => item.value,
  }));
  return (
    <Dialog
      data-testid="gds-vp-aff-end-user-dialog"
      maxWidth="md"
      onClose={props.handleClose}
      open={true}
    >
      <DialogTitle>
        {translate(
          props.isAffiliated
            ? 'VendorProgram.AffiliatedEndUsers'
            : 'VendorProgram.EndUsersListTitle'
        )}
        <IconButton
          data-testid="gds-vp-aff-end-user-dialog-close-btn"
          onClick={props.handleClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <DataGrid
          autoHeight
          columnBuffer={columns.length}
          columns={columns}
          components={{ NoRowsOverlay: NoRowsOverlay }}
          data-testid="gds-vp-aff-end-user-dialog-data-grid"
          disableColumnFilter
          disableColumnMenu
          disableColumnSelector
          disableSelectionOnClick
          getRowHeight={() => 'auto'}
          getRowId={(row: GridRowModel) =>
            props.isAffiliated ? row.endUserAffiliationId : row.endUserNumber
          }
          hideFooterPagination
          loading={EnduserAffiliationListData.isLoading}
          rows={rows as any}
          sx={{
            '.MuiDataGrid-columnHeader--moving': {
              backgroundColor: 'transparent',
            },
            '.MuiDataGrid-footerContainer>div': {
              flexWrap: 'wrap',
            },
            '.MuiDataGrid-footerContainer>div>div': {
              flex: 1,
            },
            '.MuiDataGrid-row': {
              minHeight: '52px !important',
            },
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button
          data-testid="gds-vp-aff-end-user-dialog-okey-btn"
          onClick={props.handleClose}
          sx={{ textTransform: 'none' }}
          variant="contained"
        >
          {translate('Common.Okay')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
